/**
 * Agent context for the dashboard: which screen the user is on (and what is
 * open on it), the drawer state, the selected agent profile and the agent
 * metadata for this user.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { agentApi } from './agentApi';
import { canUseAgent } from './agentContextMap';

const AgentContext = createContext(null);

const readLocal = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const writeLocal = (key, value) => {
  try {
    if (value === null || value === undefined) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch (error) {
    // storage unavailable (private mode): keep state in memory only
  }
};

export function AgentProvider({ user, activeTab, tabLabel, children }) {
  const enabled = canUseAgent(user);
  const [open, setOpen] = useState(false);
  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState(null);
  const [registrations, setRegistrations] = useState({});
  const [draft, setDraft] = useState('');
  const drawerKey = `dagent.drawerSession.${user?.id || 'anon'}`;
  const [drawerSessionId, setDrawerSessionIdState] = useState(() => readLocal(drawerKey));
  const [profileId, setProfileIdState] = useState(() => readLocal('dagent.profile'));

  const refreshMeta = useCallback(async () => {
    if (!enabled) return;
    try {
      setMeta(await agentApi.meta());
      setMetaError(null);
    } catch (error) {
      setMetaError(error.message);
    }
  }, [enabled]);

  useEffect(() => {
    refreshMeta();
  }, [refreshMeta]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'j') {
        event.preventDefault();
        setOpen((value) => !value);
      }
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled]);

  // Toasts sit top-right above everything; move them clear of the open drawer.
  useEffect(() => {
    document.body.classList.toggle('dagent-drawer-open', open);
    return () => document.body.classList.remove('dagent-drawer-open');
  }, [open]);

  const setDrawerSessionId = useCallback(
    (id) => {
      setDrawerSessionIdState(id);
      writeLocal(drawerKey, id);
    },
    [drawerKey],
  );

  const setProfileId = useCallback((id) => {
    setProfileIdState(id);
    writeLocal('dagent.profile', id);
  }, []);

  const registerContext = useCallback((key, context) => {
    setRegistrations((current) => ({ ...current, [key]: context }));
  }, []);

  const unregisterContext = useCallback((key) => {
    setRegistrations((current) => {
      if (!(key in current)) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  }, []);

  const openAgent = useCallback(
    ({ prompt, newConversation } = {}) => {
      if (newConversation) setDrawerSessionId(null);
      if (prompt) setDraft(prompt);
      setOpen(true);
    },
    [setDrawerSessionId],
  );

  const pageContext = useMemo(() => {
    const merged = { tab: activeTab, tabLabel };
    for (const context of Object.values(registrations)) Object.assign(merged, context);
    return merged;
  }, [activeTab, tabLabel, registrations]);

  const value = useMemo(
    () => ({
      user,
      enabled,
      meta,
      metaError,
      refreshMeta,
      open,
      setOpen,
      openAgent,
      activeTab,
      pageContext,
      registerContext,
      unregisterContext,
      drawerSessionId,
      setDrawerSessionId,
      draft,
      setDraft,
      profileId,
      setProfileId,
    }),
    [user, enabled, meta, metaError, refreshMeta, open, openAgent, activeTab, pageContext, registerContext, unregisterContext, drawerSessionId, setDrawerSessionId, draft, profileId, setProfileId],
  );

  return <AgentContext.Provider value={value}>{children}</AgentContext.Provider>;
}

export const useAgent = () => useContext(AgentContext);

/**
 * Lets any component inside the dashboard tell the agent what the user is
 * looking at, e.g. useAgentPageContext({ entity: { type: 'booking', id, label } }).
 * Safe to call outside the provider (does nothing).
 */
export function useAgentPageContext(context) {
  const agent = useContext(AgentContext);
  const keyRef = useRef(`ctx-${Math.random().toString(36).slice(2)}`);
  const serialized = JSON.stringify(context || null);
  const register = agent?.registerContext;
  const unregister = agent?.unregisterContext;

  useEffect(() => {
    if (!register || !unregister || serialized === 'null') return undefined;
    const key = keyRef.current;
    register(key, JSON.parse(serialized));
    return () => unregister(key);
  }, [register, unregister, serialized]);
}
